import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildScopeRows,
  renderDiagnosisSection,
  renderServiceOrderSections,
  renderExtraFields,
  renderPhotosSection,
  renderSignatureSection,
  renderFooter,
  detectDocumentKind,
  formatDocument,
  renderLogoWatermark,
} from './shared';

/**
 * "Proposta (sem valores)" — o escopo do serviço, sem nenhum preço.
 *
 * ─── PARA QUE ELE EXISTE ────────────────────────────────────────────────────
 *
 * Dois usos reais, os dois hoje impossíveis com os outros templates: mandar a
 * proposta técnica para o cliente aprovar o ESCOPO antes de discutir preço, e
 * entregar uma via sem valores para o cliente repassar a terceiros (síndico,
 * seguradora, comprador) sem expor quanto ele está pagando.
 *
 * ─── O QUE ELE NÃO IMPRIME, E POR QUÊ ───────────────────────────────────────
 *
 * • **Colunas de preço, desconto e total do item.** A tabela vem de
 *   `buildScopeRows`, uma função PRÓPRIA — não uma opção "sem preço" do
 *   `buildItemRows`. Uma flag desse tipo é o que alguém inverte sem querer, e o
 *   defeito seria expor o valor no documento em que o prestador escolheu
 *   escondê-lo, que é o oposto exato do que a feature promete.
 *
 * • **Bloco de TOTAL.** Óbvio, e é o que dá nome ao modelo.
 *
 * • **QR do PIX.** Menos óbvio e mais importante: o QR carrega o valor a pagar.
 *   Um documento que esconde o preço e ao mesmo tempo oferece o pagamento de um
 *   número que ele não mostra é contraditório — e o cliente escanearia sem saber
 *   o que está pagando.
 *
 * • **Formas aceitas / condições de pagamento.** Pelo mesmo motivo: quem manda
 *   uma proposta sem valores não está tratando de pagamento ainda.
 *
 * ─── O QUE ELE MANTÉM ───────────────────────────────────────────────────────
 *
 * Tudo o que descreve o TRABALHO: cabeçalho, dados do cliente, validade,
 * diagnóstico técnico da OS, campos extras, garantia, observações, termos,
 * fotos e a assinatura. Um documento de escopo precisa ser assinável.
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'água: PRIMEIRA instrução de desenho da página (jsPDF não tem
  // z-index; desenhada depois, ela cobriria a tabela em vez de ficar atrás).
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const { primaryColor, grayLight, grayText, darkText } = buildPalette(data.brandColorRGB);
  const contentWidth = pageWidth - margin * 2;

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // === CABEÇALHO ===
  let yPos = margin + 4;

  doc.setTextColor(...darkText);
  doc.setFont('helvetica', 'bold');
  const tituloCabecalho = fitHeaderTitle(
    doc,
    resolveDocumentTitle(data),
    contentWidth - (logoBase64 ? 24 : 0),
    20,
  );
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, margin, yPos);
  // Subtítulo: 0 de avanço quando não há.
  yPos += renderDocumentSubtitle(doc, data, {
    x: margin,
    y: yPos,
    maxWidth: contentWidth - (logoBase64 ? 24 : 0),
    color: grayText,
  });

  if (logoBase64) {
    try {
      const logoSize = 20;
      doc.addImage(
        logoBase64,
        getJsPdfImageFormat(logoBase64),
        pageWidth - margin - logoSize,
        margin,
        logoSize,
        logoSize,
      );
    } catch {
      /* segue sem a logo */
    }
  }

  yPos += 8;
  if (data.company?.name) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text(data.company.name, margin, yPos);
    yPos += 5;
  }

  const companyLines: string[] = [];
  if (data.company?.cnpj) companyLines.push(`CNPJ ${data.company.cnpj}`);
  if (data.company?.address) companyLines.push(data.company.address);
  const contact: string[] = [];
  if (data.company?.phone) contact.push(data.company.phone);
  if (data.company?.email) contact.push(data.company.email);
  if (contact.length) companyLines.push(contact.join(' · '));

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  companyLines.forEach((line) => {
    doc.text(doc.splitTextToSize(line, contentWidth - 30)[0], margin, yPos);
    yPos += 4;
  });

  yPos += 4;
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.6);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  // === CLIENTE ===
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkText);
  doc.text('PARA', margin, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(data.client.name || 'Não informado', margin + 16, yPos);

  doc.setFont('helvetica', 'bold');
  doc.text('DATA', pageWidth - margin - 40, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatQuoteDate(data.quoteDate), pageWidth - margin - 22, yPos);
  yPos += 5;

  const linhaCliente = (rotulo: string, texto: string) => {
    doc.setFont('helvetica', 'bold');
    doc.text(rotulo, margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(doc.splitTextToSize(texto, contentWidth - 20)[0], margin + 16, yPos);
    yPos += 5;
  };

  if (data.client.phone) linhaCliente('TEL', data.client.phone);
  if (data.client.document) {
    linhaCliente(
      detectDocumentKind(data.client.document) === 'cnpj' ? 'CNPJ' : 'CPF',
      formatDocument(data.client.document),
    );
  }
  if (data.client.address) linhaCliente('END', data.client.address);
  if (data.validityDays && data.quoteDate) {
    linhaCliente('VÁLIDO', `até ${calcValidityDate(data.quoteDate, data.validityDays)}`);
  }
  if (data.reference) linhaCliente('REF', data.reference);

  yPos += 6;

  // === ESCOPO (sem nenhuma coluna de dinheiro) ===
  const { headers, body } = buildScopeRows(data.items);

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    theme: 'plain',
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: darkText,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
      lineColor: darkText,
      lineWidth: { bottom: 0.5, top: 0, left: 0, right: 0 },
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: grayLight,
      lineWidth: { bottom: 0.2, top: 0, left: 0, right: 0 },
    },
    columnStyles: {
      // A descrição fica com toda a largura que sobra — sem as colunas de
      // dinheiro há bastante espaço, e é o escopo que este documento vende.
      0: { cellWidth: 'auto' as const, halign: 'left' as const },
      1: { cellWidth: 18, halign: 'center' as const },
      2: { cellWidth: 16, halign: 'center' as const },
    },
    margin: { left: margin, right: margin },
  });

  yPos = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  // Diagnóstico técnico da OS (nada de valores aqui).
  yPos = renderServiceOrderSections(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = renderDiagnosisSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // Campos extras do prestador: 0 de avanço quando não há (#L5).
  yPos = renderExtraFields(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // === OBSERVAÇÕES / GARANTIA / TERMOS ===
  if (data.warranty || data.notes || data.termsConditions) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text('Observações', margin, yPos);
    yPos += 5;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);

    for (const texto of [
      data.warranty ? `Garantia: ${data.warranty}` : null,
      data.notes ?? null,
      data.termsConditions ?? null,
    ]) {
      if (!texto) continue;
      const lines = doc.splitTextToSize(texto, contentWidth) as string[];
      if (yPos + 4 * lines.length > pageHeight - 45) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(lines, margin, yPos);
      yPos += 4 * lines.length + 2;
    }
    yPos += 4;
  }

  yPos = await renderPhotosSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
    grayLight,
  });

  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    contentBottom: yPos,
  });

  renderFooter(doc, data, { margin, pageWidth, pageHeight, grayText, primaryColor });
}
